import axios from "axios";
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { money, tryValue } from "../lib/format";
import { fmtDisplay } from "../lib/currency";
import { useAuth } from "../context/AuthContext";
import { TransactionRow } from "./TransactionRow";
import EditTransactionDialog from "./EditTransactionDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransactionList({ transactions, filters, setFilters, groups, allGroups, accounts = [], month, allTime = false, rate = 0, currency = "TRY", rates = {}, onChanged }) {
  const { can } = useAuth();
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [source, setSource] = useState("");
  const [picked, setPicked] = useState([]);
  const [bulkGroup, setBulkGroup] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const togglePick = id => setPicked(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);

  const rows = transactions.filter(x =>
    !source || (source === "erp" ? !!x.erp_key : !x.erp_key));
  const erpCount = transactions.filter(x => !!x.erp_key).length;

  const f = value => fmtDisplay(value, currency, rates);
  const togglePaid = async x => {
    await axios.patch(`${API}/transactions/${x.id}/paid`, { paid: x.paid === false });
    onChanged?.();
  };

  const sums = rows.reduce((a, x) => {
    a[x.kind] += tryValue(x, rate);
    return a;
  }, { income: 0, expense: 0 });

  const exportXls = async () => {
    setExporting(true); setExportError("");
    try {
      const response = await axios.post(`${API}/transactions/export.xls`, {
        ids: rows.map(x => x.id), month: allTime ? null : month, fallback_rate: rate,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url; link.download = `finans-${allTime ? "tum-zamanlar" : month}.xls`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      let message = "XLS dosyası indirilemedi. Lütfen tekrar deneyin.";
      try {
        const body = JSON.parse(await error.response.data.text());
        if (typeof body.detail === "string") message = body.detail;
      } catch { /* Keep the readable default for network or non-JSON errors. */ }
      setExportError(message);
    } finally { setExporting(false); }
  };

  const pickedKinds = new Set(rows.filter(x => picked.includes(x.id)).map(x => x.kind));
  const mixedPick = pickedKinds.size > 1;
  const bulkOptions = mixedPick ? [] : (pickedKinds.has("income") ? (groups.income || []) : (groups.expense || []));

  const applyBulkGroup = async () => {
    setBulkBusy(true);
    try {
      await axios.patch(`${API}/transactions/bulk-group`, { ids: picked, group: bulkGroup });
      setPicked([]);
      setBulkGroup("");
      onChanged?.();
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmDelete = async () => {
    await axios.delete(`${API}/transactions/${pendingDelete.id}`);
    setPendingDelete(null);
    onChanged?.();
  };

  return (
    <section className="panel transactions-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">HAREKETLER</p>
          <h3>Son kayıtlar</h3>
          <small className="hint" data-testid="list-counts">
            {rows.filter(x => x.kind === "expense").length} gider · {rows.filter(x => x.kind === "income").length} gelir
            {!!erpCount && ` · ${erpCount} kayıt satış yazılımından geldi`}
          </small>
        </div>
        <div className="actions">
          <select data-testid="kind-filter" value={filters.kind} onChange={e => setFilters({ ...filters, kind: e.target.value })}>
            <option value="">Tüm türler</option>
            <option value="income">Gelir</option>
            <option value="expense">Gider</option>
          </select>
          <select data-testid="source-filter" value={source} onChange={e => setSource(e.target.value)}>
            <option value="">Tüm kaynaklar</option>
            <option value="manual">Elle girilenler</option>
            <option value="erp">Satış yazılımı</option>
          </select>
          <select data-testid="group-filter" value={filters.group} onChange={e => setFilters({ ...filters, group: e.target.value })}>
            <option value="">Tüm gruplar / kalemler</option>
            {allGroups.map(g => <option key={g}>{g}</option>)}
          </select>
          <select data-testid="account-filter" value={filters.account || ""} onChange={e => setFilters({ ...filters, account: e.target.value })}>
            <option value="">Tüm hesaplar</option>
            {accounts.map(a => <option key={a} value={a}>{a}</option>)}
            <option value="__none__">Atanmamış</option>
          </select>
          <button type="button" className="outline-btn" data-testid="export-xls-button" onClick={exportXls} disabled={exporting}>
            {exporting ? <Loader2 size={16} className="spin"/> : <Download size={16}/>} {exporting ? "Hazırlanıyor…" : "XLS indir"}
          </button>
        </div>
      </div>
      {exportError && <div className="notice error" role="alert" data-testid="export-xls-error">{exportError}</div>}
      {can("edit") && !!rows.length && (
        <div className="bulk-bar" data-testid="bulk-group-bar">
          <label className="check-line">
            <input type="checkbox" data-testid="bulk-select-all"
              checked={!!picked.length && picked.length === rows.length}
              onChange={e => setPicked(e.target.checked ? rows.map(x => x.id) : [])}/>
            <span>{picked.length ? `${picked.length} kayıt seçili` : "Tümünü seç"}</span>
          </label>
          <select data-testid="bulk-group-select" value={bulkGroup} disabled={!picked.length || mixedPick}
            onChange={e => setBulkGroup(e.target.value)}>
            <option value="">Grup seç…</option>
            {bulkOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="primary-btn" data-testid="bulk-group-apply"
            disabled={!picked.length || !bulkGroup || bulkBusy || mixedPick} onClick={applyBulkGroup}>
            {mixedPick ? "Gelir ve gider birlikte seçilemez" : "Seçilenleri gruba ata"}
          </button>
          {!!picked.length && (
            <button className="outline-btn small" data-testid="bulk-clear" onClick={() => setPicked([])}>Seçimi temizle</button>
          )}
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th className="pick-col"></th><th>Tür</th><th>Açıklama</th><th>Grup</th><th>Tarih</th><th>Kur</th><th className="right">Tutar</th><th className="right">{currency} karşılığı</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(x => (
              <TransactionRow key={x.id} tx={x} rate={rate} currency={currency} rates={rates} can={can}
                picked={picked.includes(x.id)} onPick={togglePick}
                onTogglePaid={togglePaid} onEdit={setEditing} onDelete={setPendingDelete}/>
            ))}
          </tbody>
          {!!rows.length && (
            <tfoot>
              <tr data-testid="transaction-totals-row">
                <td colSpan={6}>
                  <b>Listelenen {rows.length} kaydın {currency} toplamı</b>
                  <small className="sub-line">Her kayıt kendi işlem tarihindeki kurla TL'ye, sonra seçili para birimine çevrilir</small>
                </td>
                <td className="right"><small>Gelir / Gider</small></td>
                <td className="right foot-total">
                  <span className="amount income" data-testid="list-total-income">+{f(sums.income)}</span>
                  <span className="amount expense" data-testid="list-total-expense">−{f(sums.expense)}</span>
                  <span className="foot-net" data-testid="list-total-net">Net {f(sums.income - sums.expense)}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {!rows.length && <div className="empty-table" data-testid="transactions-empty">
          {transactions.length ? "Seçili filtrelerde kayıt yok. Filtreleri sıfırlayın." : "Henüz kayıt yok. Sağdaki formdan ilk hareketi ekleyin."}
        </div>}
      </div>

      <EditTransactionDialog transaction={editing} groups={groups} accounts={accounts} onClose={() => setEditing(null)} onSaved={onChanged}/>

      <AlertDialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kayıt silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && <><b>{pendingDelete.description}</b> — {money(pendingDelete.amount, pendingDelete.currency)} ({pendingDelete.transaction_date}). Bu işlem geri alınamaz; günlüğe kaydedilir.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" className="danger-action" onClick={confirmDelete}>Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
